import type { GeoCheck, GeoMixEntry, MatchingLink, UnresolvedLink, UrlId } from './types.js';
import { fmtInt, fmtMoney } from './format.js';

/**
 * One verdict per match card (P2f). The owner duplicates Airman destinations
 * as templates, so cards with identical names are common and a match must be
 * judgeable without reading the name: the heading leads with the Airman id,
 * the verdict chip says whether the link is right, and the facts line shows
 * the five checks in a fixed order as PAIRS OF VALUES (Airman's, then the
 * account's) so the person matches them by eye rather than trusting a mark. Everything here is the existing logic
 * (owner mismatch, the geo check, the money) rearranged; only `traffic` is
 * new, and it is the geo check's own total.
 *
 * Verdict precedence: an error beats a warning, and within each the checks
 * are read in the order they are printed - account, feed, owner, then
 * traffic (no stats makes the geo check meaningless, so it is reported
 * first), then geo. A confirmed link reads "Confirmed" over the same facts.
 */
export type VerdictTone = 'ok' | 'warn' | 'error';

export interface Verdict {
  label: 'Ready' | 'Confirmed' | 'Channel ≠ URL' | 'Geo differs' | 'No stats yet' | 'Owner differs' | 'Feed differs' | 'No account';
  tone: VerdictTone;
  /** sort key: Ready first, then warnings, then errors. */
  order: 0 | 1 | 2;
}

export interface MatchFacts {
  hasAccount: boolean;
  feedMatches: boolean;
  ownerMismatch: boolean;
  /** null = the destination declares no geo (any), or the feed's rows carry no country or no searches to judge by, so there is nothing to check. */
  feedHasDestGeo: boolean | null;
  /** false = Airman's channel is not among the ids in the destination's own URL (the provider reports under the URL's id); null = nothing to compare */
  urlAgrees: boolean | null;
  /** the feed has reported something: a searches count, a payout, or any row at all */
  hasStats: boolean;
  status: string;
}

export function matchVerdict(f: MatchFacts): Verdict {
  if (!f.hasAccount) return { label: 'No account', tone: 'error', order: 2 };
  if (!f.feedMatches) return { label: 'Feed differs', tone: 'error', order: 2 };
  if (f.ownerMismatch) return { label: 'Owner differs', tone: 'error', order: 2 };
  // A channel its own URL contradicts is Airman's to correct before the link is trusted (owner ask 2026-09-16).
  if (f.urlAgrees === false) return { label: 'Channel ≠ URL', tone: 'warn', order: 1 };
  if (!f.hasStats) return { label: 'No stats yet', tone: 'warn', order: 1 };
  if (f.feedHasDestGeo === false) return { label: 'Geo differs', tone: 'warn', order: 1 };
  if (f.status === 'confirmed') return { label: 'Confirmed', tone: 'ok', order: 0 };
  return { label: 'Ready', tone: 'ok', order: 0 };
}

/** A feed has stats once it reported anything: searches, a payout, or a row (review 2026-09-18: a feed paying without a searches column read "No stats yet"). */
export function feedHasStats(l: { feedTotalSearches: number; payout7d: number; feedLastDay: string | null }): boolean {
  return l.feedTotalSearches > 0 || l.payout7d > 0 || l.feedLastDay != null;
}

/** Whether the feed's rows can judge the destination's geo at all: they carry a country and searches. A feed reporting no country (an email report) or no searches (payout only) judges nothing (review 2026-09-18). */
export function geoJudgeable(l: { feedGeo: string | null; feedTotalSearches: number }): boolean {
  return l.feedGeo != null && l.feedTotalSearches > 0;
}

export function linkFacts(l: MatchingLink): MatchFacts {
  return {
    hasAccount: l.accountId != null,
    feedMatches: l.feedMatches,
    ownerMismatch: l.ownerMismatch,
    // Share-weighted (owner note 2026-09-15): a destination spread over 30
    // countries is not "geo differs" because a 1% country has no searches.
    feedHasDestGeo: l.feedHasDestGeo === null || !geoJudgeable(l) ? null : geoServed(l.geo, l.geoMix, l.geoChecks).ok,
    urlAgrees: channelDoubt(l) ? false : l.urlAgrees === false ? null : l.urlAgrees,
    hasStats: feedHasStats(l),
    status: l.status,
  };
}

/**
 * Whether the feed serves the destination's declared countries, weighted by
 * the destination's own split: the primary country must be served, and the
 * declared share with no searches at all must stay under a fifth. A
 * destination with one declared country is judged on that country alone.
 */
export function geoServed(
  geo: string | null,
  mix: GeoMixEntry[] | null,
  checks: GeoCheck[],
): { declared: number; served: number; missing: string[]; missingShare: number; ok: boolean } {
  const declared = checks.length;
  const served = checks.filter((c) => c.searches > 0).length;
  const missing = checks.filter((c) => c.searches === 0).map((c) => c.geo);
  const shareOf = (g: string): number => {
    if (mix && mix.length) return mix.find((m) => m.geo === g)?.share ?? 0;
    return declared ? 1 / declared : 0;
  };
  const missingShare = missing.reduce((n, g) => n + shareOf(g), 0);
  const primary = geo ?? (mix && mix.length ? mix[0]!.geo : null);
  const primaryMissing = primary != null && missing.includes(primary);
  return { declared, served, missing, missingShare, ok: declared === 0 ? true : !primaryMissing && missingShare < 0.2 };
}

/** `US +31 geos` for a destination spread over 32 countries, else the single geo, else 'any'. */
export function compactGeo(geo: string | null, mix: GeoMixEntry[] | null): string {
  if (mix && mix.length > 1) return `${geo ?? mix[0]!.geo} +${mix.length - 1} geos`;
  return geo ?? 'any';
}

/** Suggested links ordered by verdict - Ready, then warnings, then errors - and by id within a group (stable). */
export function orderByVerdict<T extends { id: number }>(links: T[], verdictOf: (l: T) => Verdict): T[] {
  return [...links].sort((a, b) => verdictOf(a).order - verdictOf(b).order || a.id - b.id);
}

/** The cards "Confirm all ready (n)" would confirm: exactly the ones showing Ready. */
export function readyLinks(links: MatchingLink[]): MatchingLink[] {
  return links.filter((l) => matchVerdict(linkFacts(l)).label === 'Ready');
}

/** The pill class for a tone: green, amber, red. */
export function toneClass(tone: VerdictTone): string {
  return tone === 'ok' ? 'pill ok' : tone === 'warn' ? 'pill gold' : 'pill warn';
}

export type FactState = 'same' | 'differs' | 'unknown' | 'plain';

/**
 * One check as a pair of values the person matches by eye: Airman's side on
 * the left, the account's or the feed's on the right. No marks - the values
 * ARE the check; colour only says whether the two agree (red = differ, muted
 * = nothing to compare yet). A single-valued fact (traffic, money) has no
 * right side.
 */
export interface Fact {
  key: 'feed' | 'provider' | 'owner' | 'type' | 'geo' | 'traffic' | 'money';
  label: string;
  /** Airman's value, or the only value. */
  left: string;
  /** The account's or feed's value; null for a single-valued fact. */
  right: string | null;
  state: FactState;
  title: string;
}

/** The fact as one string: `label left → right`. */
export const factText = (f: Fact): string => `${f.label} ${f.left}${f.right != null ? ` → ${f.right}` : ''}`;

/** The owner as Airman wrote it, with the rep it resolves to when that is a different word. */
const ownerAsWritten = (owner: string | null, ownerRep: string | null): string => {
  const raw = owner ?? '?';
  return ownerRep && owner && ownerRep.trim().toLowerCase() !== owner.trim().toLowerCase() ? `${raw} (${ownerRep})` : raw;
};

/** `US 64% · GB 33%` for a multi-country destination, else the single geo, else 'any'. */
export function declaredGeo(geo: string | null, mix: GeoMixEntry[] | null): string {
  if (mix && mix.length) return mix.map((m) => `${m.geo} ${Math.round(m.share * 100)}%`).join(' · ');
  return geo ?? 'any';
}

/**
 * The geo pair: Airman's declared country set on the left (the mix, when
 * there is one), and on the right what the feed carries of EACH declared
 * country - `GB 1,200 / 1,200`, `US 800, no DE / 1,200` - so a multi-country
 * destination is judged country by country, never on the primary alone.
 */
function geoFact(geo: string | null, mix: GeoMixEntry[] | null, checks: GeoCheck[], total: number, destSearches: number, feedGeoLabel: string): Fact {
  // The declared side is compact - a destination spread over 30 countries
  // reads `US +29`, never a list; the full split is in the title (owner note
  // 2026-09-15: several geos on one destination id is the norm now).
  const left = compactGeo(geo, mix);
  const declaredList = declaredGeo(geo, mix);
  if (total <= 0) {
    return { key: 'geo', label: 'geo', left, right: 'no searches reported', state: 'unknown', title: `declared ${declaredList}; the feed reports no searches to check the geo against` };
  }
  if (feedGeoLabel === 'no country') {
    return { key: 'geo', label: 'geo', left, right: 'no country reported', state: 'unknown', title: `declared ${declaredList}; the feed's rows carry no country, so the geo cannot be checked` };
  }
  if (checks.length === 0) {
    return { key: 'geo', label: 'geo', left: 'any', right: feedGeoLabel, state: 'same', title: `the destination declares no geo, so any feed geo is right; the feed is mostly ${feedGeoLabel}` };
  }
  const g = geoServed(geo, mix, checks);
  const right = checks.length === 1
    ? g.missing.length
      ? `no ${g.missing[0]} / ${fmtInt(total)}`
      : `${checks[0]!.geo} ${fmtInt(checks[0]!.searches)} / ${fmtInt(total)}`
    : `${feedGeoLabel} · ${g.served}/${g.declared} declared served`;
  const title = g.missing.length
    ? `declared ${declaredList}; none of the feed's ${fmtInt(total)} searches are ${g.missing.join(', ')} (${Math.round(g.missingShare * 100)}% of the declared split); the feed is mostly ${feedGeoLabel}` +
      (g.ok ? ' - small share, not a mismatch' : '')
    : `declared ${declaredList}; ${fmtInt(destSearches)} of the feed's ${fmtInt(total)} searches are in the declared countries; the feed is mostly ${feedGeoLabel}`;
  return { key: 'geo', label: 'geo', left, right, state: g.ok ? 'same' : 'differs', title };
}

/**
 * Whether a URL that contradicts the channel is worth a verdict: a source
 * carries the feed the URL names, or no source carries the channel's number
 * at all. A URL id nobody reports beside a channel with stats is a note, not
 * a flag (live, 2026-09-17: a tracking parameter read as a feed id).
 */
export function channelDoubt(d: { urlAgrees: boolean | null; urlFeedKnown: boolean; feedTotalSearches: number; payout7d: number; feedLastDay: string | null }): boolean {
  return d.urlAgrees === false && (d.urlFeedKnown || !feedHasStats(d));
}

/** The ids a URL carries, as words: `campaign_id=388389, pub_id=1917`. */
const urlIdsText = (ids: readonly UrlId[]): string => ids.map((i) => `${i.name}=${i.value}`).join(', ');

/** What the URL says the feed is: the one id it names, else every id it carries. */
const urlSays = (d: { urlIds: UrlId[]; urlFeedId: string | null }): string | null =>
  d.urlFeedId ?? (d.urlIds.length ? d.urlIds.map((i) => i.value).join(' / ') : null);

/**
 * Airman's channel against the feed the link points to, with the
 * destination's own URL as the double-check (owner ask 2026-09-16): a
 * channel the URL contradicts reads red with both numbers on the face, a
 * number the URL supplied (no channel in Airman) is named as the URL's.
 */
function feedFact(d: { channel: string | null; channelSource: 'channel' | 'url' | 'offer'; urlIds: UrlId[]; urlFeedId: string | null; urlAgrees: boolean | null; urlFeedKnown: boolean; feedTotalSearches: number; payout7d: number; feedLastDay: string | null }, feedId: string): Fact {
  const ids = urlIdsText(d.urlIds);
  const doubt = channelDoubt(d);
  if (!d.channel) {
    return { key: 'feed', label: 'feed', left: '?', right: feedId, state: 'unknown', title: `Airman carries no channel for this destination${ids ? ` and its URL carries ${ids}` : ''}; the link points to feed ${feedId}` };
  }
  if (d.channelSource === 'url') {
    const same = d.channel.trim().toLowerCase() === feedId.trim().toLowerCase();
    return { key: 'feed', label: 'feed', left: `URL ${d.channel}`, right: feedId, state: same ? 'same' : 'differs', title: `Airman carries no channel for this destination; its URL names feed ${d.channel} (${ids})${same ? '' : ` - the link points to feed ${feedId}`}` };
  }
  if (doubt) {
    return {
      key: 'feed',
      label: 'feed',
      left: `${d.channel} · URL ${urlSays(d) ?? '?'}`,
      right: feedId,
      state: 'differs',
      title: `Airman's channel is ${d.channel} but the destination's URL carries ${ids}${d.urlFeedKnown ? ` - a source reports ${urlSays(d)}` : ' - and no source reports the channel'}, so check the channel in Airman`,
    };
  }
  const same = d.channel.trim().toLowerCase() === feedId.trim().toLowerCase();
  const agrees = d.urlAgrees === true ? ' - the URL agrees' : d.urlAgrees === false ? ` - the URL also carries ${ids}, which no source reports` : '';
  return {
    key: 'feed',
    label: 'feed',
    left: d.channel,
    right: feedId,
    state: same ? 'same' : 'differs',
    title: same ? `Airman's channel ${d.channel} is the feed id the stats carry${agrees}` : `Airman's channel is ${d.channel}; the link points to feed ${feedId} - check they are the same feed (a prefix like PN is Airman's naming)${agrees}`,
  };
}

/** The destination's network against the feed's type: the network IS the suggested type, set when the link is confirmed. */
function typeFact(network: string | null, feedType: string | null): Fact {
  if (!network) {
    return { key: 'type', label: 'type', left: '?', right: feedType ?? 'unclassified', state: 'unknown', title: 'Airman carries no network for this destination; the feed type is set by hand under Stats' };
  }
  if (!feedType) {
    return { key: 'type', label: 'type', left: network, right: `${network} (suggested)`, state: 'unknown', title: `the feed has no type yet; confirming the link sets it to ${network} from Airman's network` };
  }
  const same = network.trim().toLowerCase() === feedType.trim().toLowerCase();
  return { key: 'type', label: 'type', left: network, right: feedType, state: same ? 'same' : 'differs', title: same ? `Airman's network and the feed's type agree` : `Airman's network is ${network} but the feed is classified ${feedType} under Stats` };
}

/** The seven facts in their fixed order, for a link with an account: feed, provider, owner, type, geo, traffic, money. */
export function linkFactLine(l: MatchingLink): Fact[] {
  // A mechanism account (a sheet, an upload, an inbox) is named by its own
  // label - "Mobilynx G Sheet", never "generic" - since a sheet can carry
  // any provider's feed (2026-09-16).
  const mechanism = l.provider != null && ['generic', 'upload', 'email'].includes(l.provider);
  const account = mechanism ? (l.accountLabel || l.accountProviderLabel || l.provider || '?') : (l.provider ?? '?');
  const sameWord = l.feedProvider.trim().toLowerCase() === (l.provider ?? '').trim().toLowerCase();
  const provider: Fact = {
    key: 'provider',
    label: 'provider',
    left: l.feedProvider,
    right: account,
    state: l.accountId == null ? 'unknown' : l.feedMatches ? 'same' : 'differs',
    title: l.feedMatches
      ? sameWord
        ? `Airman pushed this feed as ${l.feedProvider}, the account's provider`
        : mechanism
          ? `Airman pushed this feed as ${l.feedProvider}; the source is ${account}, which can carry any provider's feed`
          : `Airman pushed this feed as ${l.feedProvider}, which the alias map resolves to ${account} - the account's provider`
      : `Airman pushed this feed as ${l.feedProvider}; the account's provider is ${l.provider ?? 'unknown'}`,
  };
  const owner: Fact = {
    key: 'owner',
    label: 'owner',
    // The relationship as Airman names it (LOKI, David) leads; the owner email and the rep it resolves to follow in the title.
    left: l.relationship ?? ownerAsWritten(l.owner, l.ownerRep),
    right: l.rep ?? '?',
    state: l.ownerMismatch ? 'differs' : l.ownerRep && l.rep ? 'same' : 'unknown',
    title: l.ownerMismatch
      ? `the destination's owner resolves to ${l.ownerRep} but the account belongs to ${l.rep}`
      : l.ownerRep && l.rep
        ? `owner ${l.owner} resolves to ${l.ownerRep}, who owns the account`
        : `owner ${l.owner ?? 'unknown'} is not a rep name (or alias), so it cannot be checked against ${l.rep ?? 'the account'}`,
  };
  // The feed's searches and its geo counts are ALL-TIME (read/matching.ts
  // feedS, destGeoS, geoN); the committed visits and the money are 7-day.
  // Said on the face, so the two sides of the traffic pair are not read as
  // the same window.
  const total = l.feedTotalSearches;
  const feedGeo = l.feedGeo ?? 'no country';
  const geos = l.feedGeoCount > 1 ? `${feedGeo} (${l.feedGeoCount} geos)` : feedGeo;
  const geo = geoFact(l.geo, l.geoMix, l.geoChecks, total, l.destGeoSearches, geos);
  const traffic: Fact = {
    key: 'traffic',
    label: 'traffic',
    left: `${fmtInt(l.buyCommitted7d)} committed (7d)`,
    right: total > 0 ? `${fmtInt(total)} searches (all time)` : 'no stats yet',
    state: total > 0 ? 'plain' : 'unknown',
    title:
      total > 0
        ? 'visits Airman committed to this destination over the last 7 days, against every search the feed has ever reported (the geo counts use the same all-time total)'
        : 'visits Airman committed to this destination over the last 7 days; no stats rows for this feed yet - the first crawl after it goes live fills the right side',
  };
  const money: Fact = {
    key: 'money',
    label: 'money',
    left: `${fmtMoney(l.buyCost7d)} spent`,
    right: `${fmtMoney(l.payout7d)} earned (7d)`,
    state: 'plain',
    title: 'Airman spend on this destination and USD payout on this feed over the last 7 days',
  };
  return [feedFact(l, l.feedId), provider, owner, typeFact(l.network, l.feedType), geo, traffic, money];
}

/** The dates that left the card face (P2f): in its title and under Edit. */
export function linkDates(l: MatchingLink): string {
  const parts: string[] = [];
  if (l.validFrom) parts.push(`${l.validFromOrigin === 'created' ? 'created' : 'on since'} ${l.validFrom}`);
  if (l.effectiveFrom && l.validFrom && l.effectiveFrom < l.validFrom) parts.push(`charged from ${l.effectiveFrom}`);
  if (l.validTo) parts.push(`until ${l.validTo}`);
  parts.push(`link created ${l.createdAt.slice(0, 10)}`);
  return parts.join(' · ');
}

/** The feed fact of a registration with no account: Airman's channel, and what the destination's own URL says about it. */
function openFeedFact(u: UnresolvedLink): Fact {
  const ids = urlIdsText(u.urlIds);
  // An open registration has no stats to corroborate the channel, so a URL that disagrees is always said.
  if (u.urlAgrees === false) {
    return { key: 'feed', label: 'feed', left: `${u.channel ?? u.feedId} · URL ${urlSays(u) ?? '?'}`, right: '?', state: 'differs', title: `Airman's channel is ${u.channel ?? u.feedId} but the destination's URL carries ${ids} - the provider reports under the URL's id, so the channel may be wrong; no account claims either yet` };
  }
  if (u.channelSource === 'url') {
    return { key: 'feed', label: 'feed', left: `URL ${u.feedId}`, right: '?', state: 'unknown', title: `Airman carries no channel for this destination; its URL names feed ${u.feedId} (${ids}); no account claims it yet` };
  }
  return { key: 'feed', label: 'feed', left: u.channel ?? u.feedId, right: '?', state: 'unknown', title: `Airman's channel for this destination - the id its feed's stats carry${u.urlAgrees ? ', and its URL agrees' : ''}; no account claims a feed with it yet` };
}

/** The facts line of a registration with no account yet: Airman's side only, nothing to compare with. */
export function unresolvedFactLine(u: UnresolvedLink): Fact[] {
  return [
    openFeedFact(u),
    { key: 'provider', label: 'provider', left: u.feedProvider, right: '?', state: 'unknown', title: 'Airman pushed this feed with this provider; no account claims the feed yet' },
    { key: 'owner', label: 'owner', left: u.relationship ?? ownerAsWritten(u.owner, u.ownerRep), right: '?', state: 'unknown', title: 'the relationship as Airman names it (else the owner, with the rep it resolves to); no account to compare with yet' },
    { key: 'type', label: 'type', left: u.network ?? '?', right: '?', state: 'unknown', title: 'Airman\'s network: the suggested type for the feed once linked' },
    { key: 'geo', label: 'geo', left: compactGeo(u.geo, u.geoMix), right: '?', state: 'unknown', title: `declared ${declaredGeo(u.geo, u.geoMix)}; no linked feed to compare with yet` },
    { key: 'traffic', label: 'traffic', left: `${fmtInt(u.buyCommitted14d)} committed (14d)`, right: '?', state: 'unknown', title: 'visits Airman committed to this destination over the last 14 days; no linked feed to compare with yet' },
    { key: 'money', label: 'money', left: `${fmtMoney(u.buyCost14d)} spent (14d)`, right: null, state: 'plain', title: 'Airman spend on this destination over the last 14 days' },
  ];
}
